import { useEffect, useMemo, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "../lib/api";
import { requireWorkspace } from "../lib/requireWorkspace";

import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import { Textarea } from "../components/ui/textarea";
import { Input } from "../components/ui/input";
import { Separator } from "../components/ui/separator";

export default function AdsPage() {
  const [workspace, setWorkspace] = useState(null);

  const [productOrService, setProductOrService] = useState("");
  const [goal, setGoal] = useState("sales");
  const [notes, setNotes] = useState("");
  const [ugcPlatform, setUgcPlatform] = useState("tiktok");
  const [ugcDuration, setUgcDuration] = useState("30");
  const [ugcTone, setUgcTone] = useState("authentic creator testimonial");
  const [ugcAssets, setUgcAssets] = useState("");
  const [ugcLoading, setUgcLoading] = useState(false);
  const [ugcOutput, setUgcOutput] = useState(null);
  const [scheduleCadence, setScheduleCadence] = useState("daily");
  const [scheduleTheme, setScheduleTheme] = useState("");
  const [scheduleAvatar, setScheduleAvatar] = useState("");
  const [schedulePlatforms, setSchedulePlatforms] = useState("instagram,tiktok");
  const [scheduleMessage, setScheduleMessage] = useState(null);
  const [editSource, setEditSource] = useState("");
  const [editRequest, setEditRequest] = useState("");
  const [editPlan, setEditPlan] = useState(null);
  const [editStatus, setEditStatus] = useState(null);

  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState(null);

  const [genId, setGenId] = useState(null);
  const [output, setOutput] = useState(null);
  const [nextStep, setNextStep] = useState(null);
  const resultsRef = useRef(null);

  const ready = useMemo(() => productOrService.trim().length >= 3, [productOrService]);

  useEffect(() => {
    try {
      const ws = requireWorkspace();
      setWorkspace(ws);
    } catch (e) {
      setErr(e.message || "No workspace selected.");
    }
  }, []);

  async function generate() {
    if (!workspace) return;
    setErr(null);
    setLoading(true);
    setGenId(null);
    setOutput(null);
    setNextStep(null);

    try {
      const res = await api.post(`/api/workspaces/${workspace.id}/generate/meta-ad`, {
        product_or_service: productOrService,
        goal,
        platform: "meta",
        notes,
      });

      setGenId(res.data.id);
      setOutput(res.data.output);
      setNextStep("review");
      setTimeout(() => {
        resultsRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
      }, 100);
    } catch (e) {
      const status = e?.response?.status;
      const detail = e?.response?.data?.detail;

      if (status === 402) {
        setErr(detail || "Not enough credits. Please upgrade or buy more credits.");
      } else if (status === 403) {
        setErr(detail || "This action is unavailable on your plan or currently disabled.");
      } else {
        setErr(detail || "Failed to generate ad pack. Check backend logs and try again.");
      }
    } finally {
      setLoading(false);
    }
  }

  async function generateUGC() {
    if (!workspace || !ready) return;
    setUgcLoading(true); setErr(null);
    try {
      const res = await api.post(`/api/workspaces/${workspace.id}/generate/ugc-script`, {
        product_or_service: productOrService, platform: ugcPlatform, duration_seconds: Number(ugcDuration),
        tone: ugcTone, asset_urls: ugcAssets.split(/\s*,\s*|\n/).filter(Boolean), notes,
      });
      setUgcOutput(res.data.output);
    } catch (e) { setErr(e?.response?.data?.detail || "Could not generate the UGC script."); }
    finally { setUgcLoading(false); }
  }

  async function createSchedule() {
    if (!workspace || !scheduleAvatar.trim()) return;
    setScheduleMessage(null); setErr(null);
    try {
      const res = await api.post(`/api/workspaces/${workspace.id}/content-schedules`, {
        name: `${productOrService || "Brand"} ${scheduleCadence} creative`, cadence: scheduleCadence,
        theme: scheduleTheme, avatar_key: scheduleAvatar.trim(), asset_urls: ugcAssets.split(/\s*,\s*|\n/).filter(Boolean),
        platforms: schedulePlatforms.split(/\s*,\s*/).filter(Boolean), timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
      });
      setScheduleMessage(`Schedule saved. ${res.data.publishing}.`);
    } catch (e) { setErr(e?.response?.data?.detail || "Could not save schedule."); }
  }

  async function planEdit() {
    if (!workspace || !editSource || !editRequest) return;
    setEditStatus("Planning…");
    try {
      const res = await api.post(`/api/workspaces/${workspace.id}/video-edits/ai-plan`, { source_url: editSource, request: editRequest });
      setEditPlan(res.data.plan); setEditStatus("Review the plan, then apply it.");
    } catch (e) { setEditStatus(e?.response?.data?.detail || "Could not plan edit."); }
  }

  async function applyEdit() {
    if (!workspace || !editSource || !editPlan) return;
    setEditStatus("Editing…");
    try {
      const res = await api.post(`/api/workspaces/${workspace.id}/video-edits`, { source_url: editSource, ...editPlan });
      setEditStatus(`Edit ${res.data.id} queued. Original remains unchanged.`);
    } catch (e) { setEditStatus(e?.response?.data?.detail || "Could not queue edit."); }
  }

  function copyText(text) {
    navigator.clipboard.writeText(text);
  }

  function pretty(obj) {
    return JSON.stringify(obj, null, 2);
  }

  if (!workspace) {
    return (
      <div className="space-y-4" data-testid="ads-no-workspace">
        <div className="text-2xl font-semibold">Ad Creator</div>
        <Card className="border-amber-200 bg-amber-50">
          <CardContent className="py-4">
            <div className="text-sm text-muted-foreground">{err || "No workspace selected."}</div>
            <div className="mt-3 flex gap-2">
              <Link to="/app/workspaces">
                <Button>Go to Workspaces</Button>
              </Link>
              <Link to="/app/brand">
                <Button variant="outline">Set Brand DNA</Button>
              </Link>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  const packResults = output?.results || output || {};
  const maya = packResults?.maya_metrics || {};
  const cole = packResults?.cole_creative || {};
  const river = packResults?.river_coach || packResults?.coach_river || {};

  return (
    <div className="space-y-6" data-testid="ads-page">
      <div className="flex flex-col gap-2 md:flex-row md:items-end md:justify-between">
        <div>
          <div className="text-2xl font-semibold">Ad Creator</div>
          <div className="text-sm text-muted-foreground">
            Generate a full Meta ad pack with your AI team — strategy, copy, UGC scripts, and launch checklist.
          </div>
        </div>

        <div className="flex items-center gap-2">
          <Badge variant="secondary">{workspace.name}</Badge>
          <Link to="/app/workspaces" className="text-sm text-muted-foreground hover:underline">
            change
          </Link>
        </div>
      </div>

      {err && (
        <Card className="border-red-200 bg-red-50">
          <CardContent className="py-3 text-sm text-red-700">{err}</CardContent>
        </Card>
      )}

      <div className="grid gap-4 lg:grid-cols-3">
        {/* Left: generator form */}
        <Card className="lg:col-span-2">
          <CardHeader>
            <CardTitle>Generate Meta Ad Pack</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="space-y-2">
              <label className="text-sm font-medium">Product or service</label>
              <Input
                placeholder="e.g., Mouth tape for better sleep & reduced snoring"
                value={productOrService}
                onChange={(e) => setProductOrService(e.target.value)}
                data-testid="product-input"
              />
              <div className="text-xs text-muted-foreground">
                Tip: include the outcome + who it's for for better hooks.
              </div>
            </div>

            <div className="grid gap-3 md:grid-cols-3">
              <div className="space-y-2 md:col-span-1">
                <label className="text-sm font-medium">Goal</label>
                <div className="flex flex-wrap gap-2" data-testid="ad-goals">
                  {["sales", "leads", "bookings"].map((g) => (
                    <button
                      key={g}
                      onClick={() => setGoal(g)}
                      className={[
                        "flex-1 rounded-lg border px-3 py-2 text-sm transition",
                        goal === g ? "bg-muted font-medium" : "hover:bg-muted/60",
                      ].join(" ")}
                      type="button"
                      data-testid={`goal-${g}`}
                    >
                      {g}
                    </button>
                  ))}
                </div>
              </div>

              <div className="space-y-2 md:col-span-2">
                <label className="text-sm font-medium">Notes (optional)</label>
                <Textarea
                  placeholder="Angles to focus on, offers, promos, pain points, or what to avoid."
                  value={notes}
                  onChange={(e) => setNotes(e.target.value)}
                  className="min-h-[90px]"
                  data-testid="notes-input"
                />
              </div>
            </div>

            <div className="flex flex-wrap items-center gap-3" data-testid="ad-actions">
              <Button onClick={generate} disabled={!ready || loading} data-testid="generate-btn">
                {loading ? "Generating…" : "Generate Ad Pack"}
              </Button>

              <Link to="/app/brand">
                <Button variant="outline">Review Brand DNA</Button>
              </Link>

              <Link to="/app/library" className="text-sm text-muted-foreground hover:underline">
                View Library
              </Link>
            </div>

            {genId && (
              <div className="rounded-lg border bg-background p-3" data-testid="generation-saved">
                <div className="text-sm font-medium">Saved</div>
                <div className="text-xs text-muted-foreground">Generation ID: {genId}</div>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Right: AI team panel */}
        <Card>
          <CardHeader>
            <CardTitle>Your AI Team</CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            <div className="rounded-lg border p-3">
              <div className="font-medium">Maya Metrics</div>
              <div className="text-sm text-muted-foreground">
                Platform + objective + budget + test plan.
              </div>
            </div>

            <div className="rounded-lg border p-3">
              <div className="font-medium">Cole Creative</div>
              <div className="text-sm text-muted-foreground">
                Hooks, headlines, primary text, angles, UGC scripts.
              </div>
            </div>

            <div className="rounded-lg border p-3">
              <div className="font-medium">Coach River</div>
              <div className="text-sm text-muted-foreground">
                Checklist, compliance flags, week-1 optimization.
              </div>
            </div>

            <Separator />
            <div className="text-sm text-muted-foreground">
              After you generate, review each tab and copy what you want into campaigns.
            </div>
          </CardContent>
        </Card>
      </div>

      <div className="grid gap-4 lg:grid-cols-2">
        <Card data-testid="ugc-script-generator">
          <CardHeader><CardTitle>AI UGC Script Studio</CardTitle></CardHeader>
          <CardContent className="space-y-3">
            <div className="text-sm text-muted-foreground">Create a speakable, platform-specific script grounded in your product and optional image URLs.</div>
            <div className="grid gap-3 sm:grid-cols-3">
              <label className="text-sm">Platform<select className="mt-1 w-full rounded-md border p-2" value={ugcPlatform} onChange={e => setUgcPlatform(e.target.value)}><option>tiktok</option><option>instagram</option><option>youtube</option><option>facebook</option></select></label>
              <label className="text-sm">Length<select className="mt-1 w-full rounded-md border p-2" value={ugcDuration} onChange={e => setUgcDuration(e.target.value)}><option value="15">15s</option><option value="30">30s</option><option value="45">45s</option><option value="60">60s</option></select></label>
              <label className="text-sm">Creator tone<Input className="mt-1" value={ugcTone} onChange={e => setUgcTone(e.target.value)} /></label>
            </div>
            <Input placeholder="Product image URLs (comma-separated, optional)" value={ugcAssets} onChange={e => setUgcAssets(e.target.value)} />
            <Button onClick={generateUGC} disabled={!ready || ugcLoading}>{ugcLoading ? "Writing…" : "Generate UGC Script"}</Button>
            {ugcOutput && <pre className="max-h-80 overflow-auto rounded-lg border bg-muted/20 p-3 text-xs">{JSON.stringify(ugcOutput, null, 2)}</pre>}
          </CardContent>
        </Card>
        <Card data-testid="content-scheduler">
          <CardHeader><CardTitle>Recurring Content</CardTitle></CardHeader>
          <CardContent className="space-y-3">
            <div className="text-sm text-muted-foreground">Save a recipe for automatic video creation. Publishing remains approval-gated until a social account is connected.</div>
            <div className="grid gap-3 sm:grid-cols-2">
              <label className="text-sm">Cadence<select className="mt-1 w-full rounded-md border p-2" value={scheduleCadence} onChange={e => setScheduleCadence(e.target.value)}><option>hourly</option><option>daily</option><option>weekly</option></select></label>
              <label className="text-sm">Avatar key<Input className="mt-1" placeholder="Avatar name or ID" value={scheduleAvatar} onChange={e => setScheduleAvatar(e.target.value)} /></label>
            </div>
            <Input placeholder="Theme for each scheduled creative" value={scheduleTheme} onChange={e => setScheduleTheme(e.target.value)} />
            <Input placeholder="Platforms, e.g. instagram,tiktok" value={schedulePlatforms} onChange={e => setSchedulePlatforms(e.target.value)} />
            <Button onClick={createSchedule} disabled={!scheduleAvatar.trim()}>Save Content Schedule</Button>
            {scheduleMessage && <div className="rounded-md border border-emerald-200 bg-emerald-50 p-2 text-sm text-emerald-800">{scheduleMessage}</div>}
          </CardContent>
        </Card>
      </div>

      <Card data-testid="video-editor">
        <CardHeader><CardTitle>Video Editor + AI Edit</CardTitle></CardHeader>
        <CardContent className="space-y-3">
          <div className="text-sm text-muted-foreground">Paste an OmniMark video URL, describe the change, review the AI plan, then apply it. Edits create a new MP4 and never overwrite the original.</div>
          <Input placeholder="OmniMark video URL" value={editSource} onChange={e => setEditSource(e.target.value)} />
          <Textarea placeholder="Example: trim the first 2 seconds, make it 9:16, and speed it up slightly." value={editRequest} onChange={e => setEditRequest(e.target.value)} />
          <div className="flex flex-wrap gap-2"><Button variant="outline" onClick={planEdit} disabled={!editSource || !editRequest}>Ask AI for an edit plan</Button><Button onClick={applyEdit} disabled={!editPlan}>Apply reviewed plan</Button></div>
          {editPlan && <pre className="max-h-56 overflow-auto rounded-lg border bg-muted/20 p-3 text-xs">{JSON.stringify(editPlan, null, 2)}</pre>}
          {editStatus && <div className="text-sm text-muted-foreground">{editStatus}</div>}
        </CardContent>
      </Card>

      {/* Results */}
      {output && (
        <Card ref={resultsRef} data-testid="generation-results">
          <CardHeader>
            <CardTitle className="flex items-center justify-between">
              Results
              <div className="flex gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => copyText(pretty(output))}
                >
                  Copy all JSON
                </Button>
                <Link to="/app/library">
                  <Button size="sm" variant="outline">Open Library</Button>
                </Link>
              </div>
            </CardTitle>
          </CardHeader>

          <CardContent className="space-y-4">
            {nextStep && (
              <div className="rounded-xl border bg-muted/30 p-4" data-testid="ad-next-step">
                <div className="text-sm font-semibold">Next step</div>
                <div className="mt-1 text-sm text-muted-foreground">
                  Your ad pack is ready. Review the AI team tabs, then move it into launch, video creation, or saved assets.
                </div>
                <div className="mt-3 flex flex-wrap gap-2">
                  <Button size="sm" onClick={() => copyText(pretty(output))}>
                    Copy full pack
                  </Button>
                  <Link to="/app/campaigns">
                    <Button size="sm" variant="outline">Create Campaign</Button>
                  </Link>
                  <Link to="/app/studio">
                    <Button size="sm" variant="outline">Create Studio Videos</Button>
                  </Link>
                  <Link to="/app/library">
                    <Button size="sm" variant="outline">Open Saved Pack</Button>
                  </Link>
                </div>
              </div>
            )}
            <Tabs defaultValue="maya" className="w-full">
              <TabsList className="grid w-full grid-cols-3">
                <TabsTrigger value="maya">Maya (Strategy)</TabsTrigger>
                <TabsTrigger value="cole">Cole (Creative)</TabsTrigger>
                <TabsTrigger value="river">Coach River</TabsTrigger>
              </TabsList>

              <TabsContent value="maya" className="mt-4 space-y-3">
                <SectionHeader
                  title="Strategy Recommendations"
                  subtitle="Platforms, objective, budget range, duration, success metrics."
                  onCopy={() => copyText(pretty(maya))}
                />
                <JsonCard obj={maya} />
              </TabsContent>

              <TabsContent value="cole" className="mt-4 space-y-3">
                <SectionHeader
                  title="Ad Copy + UGC Scripts"
                  subtitle="Hooks, primary text, headlines, angles, and scripts."
                  onCopy={() => copyText(pretty(cole))}
                />
                <JsonCard obj={cole} />

                <div className="grid gap-4 md:grid-cols-3">
                  <ListCard title="Hooks" items={cole?.hooks} />
                  <ListCard title="Headlines" items={cole?.headlines} />
                  <ListCard title="Angles" items={cole?.angles} />
                </div>
              </TabsContent>

              <TabsContent value="river" className="mt-4 space-y-3">
                <SectionHeader
                  title="Launch Checklist + Compliance"
                  subtitle="What to verify before launch + what to avoid."
                  onCopy={() => copyText(pretty(river))}
                />
                <JsonCard obj={river} />
                <div className="grid gap-4 md:grid-cols-2">
                  <ListCard title="Launch checklist" items={river?.launch_checklist} />
                  <ListCard title="Compliance flags" items={river?.compliance_flags} />
                </div>
              </TabsContent>
            </Tabs>
          </CardContent>
        </Card>
      )}
    </div>
  );
}

function SectionHeader({ title, subtitle, onCopy }) {
  return (
    <div className="flex flex-col gap-2 md:flex-row md:items-center md:justify-between">
      <div>
        <div className="text-lg font-semibold">{title}</div>
        <div className="text-sm text-muted-foreground">{subtitle}</div>
      </div>
      <Button variant="outline" size="sm" onClick={onCopy}>
        Copy JSON
      </Button>
    </div>
  );
}

function JsonCard({ obj }) {
  return (
    <div className="rounded-lg border bg-background p-3">
      <pre className="max-h-[420px] overflow-auto text-xs leading-relaxed">
        {JSON.stringify(obj ?? {}, null, 2)}
      </pre>
    </div>
  );
}

function ListCard({ title, items }) {
  const list = Array.isArray(items) ? items : [];
  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-base">{title}</CardTitle>
      </CardHeader>
      <CardContent className="space-y-2">
        {list.length === 0 ? (
          <div className="text-sm text-muted-foreground">No items found.</div>
        ) : (
          <ul className="space-y-2 text-sm">
            {list.slice(0, 10).map((x, idx) => (
              <li key={idx} className="rounded-md border p-2">
                {typeof x === "string" ? x : JSON.stringify(x)}
              </li>
            ))}
          </ul>
        )}
      </CardContent>
    </Card>
  );
}
